/**
 * Pure geometry ops on `LinePrompt`s. No DOM, no UI state, no THREE scene
 * touches — just math. The radial-menu / edge-op controller calls these
 * after the user picks a target; the LineMode then calls
 * `updateShapePreview()` to rebuild the polygon visualization.
 *
 * All math runs in the XY plane (outline edits are flat). Z is preserved on
 * `worldPoint` writes so the prompt stays anchored to its base plane.
 */

import * as THREE from 'three';
import { PromptAligner } from './PromptAligner';
import type { LinePrompt } from './types';

/**
 * Set `source.direction` to `targetDir` (normalized). `worldPoint` stays
 * put — the edge rotates around its midpoint.
 */
export function setEdgeParallelTo(source: LinePrompt, targetDir: THREE.Vector3): void {
  const dir = clampToXY(targetDir);
  if (!dir) return;
  source.direction.copy(dir);
}

/**
 * Set `source.direction` to a 90° rotation of `targetDir` in the XY plane.
 * `worldPoint` stays put.
 */
export function setEdgePerpendicularTo(source: LinePrompt, targetDir: THREE.Vector3): void {
  const dir = clampToXY(targetDir);
  if (!dir) return;
  source.direction.set(-dir.y, dir.x, 0);
}

/**
 * Slide `source` perpendicular to ITS OWN direction so its perpendicular
 * distance to the line through `targetOrigin` with `targetDirection` equals
 * `distance`. Sign of the current offset is preserved — `source` stays on
 * whichever side of the target line it was already on.
 *
 * If `source` is exactly on the target line (signed distance 0), it moves
 * along the target's positive normal by `distance` (arbitrary but stable).
 */
export function setEdgeDistanceTo(source: LinePrompt, targetOrigin: THREE.Vector3, targetDirection: THREE.Vector3, distance: number): void {
  const targetDir = clampToXY(targetDirection);
  if (!targetDir) return;
  // Normal to the target's line in XY.
  const targetNormal = new THREE.Vector3(-targetDir.y, targetDir.x, 0);

  // Signed perpendicular offset from targetOrigin to source.worldPoint.
  const delta = new THREE.Vector3().subVectors(source.worldPoint, targetOrigin);
  delta.z = 0;
  const signed = delta.dot(targetNormal);
  const sign = signed >= 0 ? 1 : -1;

  // Foot of perpendicular from source.worldPoint onto target's infinite line.
  const foot = source.worldPoint.clone().sub(targetNormal.clone().multiplyScalar(signed));
  // Place source at `distance` from foot along the original side.
  const placed = foot.add(targetNormal.clone().multiplyScalar(distance * sign));
  source.worldPoint.set(placed.x, placed.y, source.worldPoint.z);
}

/**
 * Average direction of N references, returned as a unit Vector3 in XY.
 * Uses `PromptAligner.averageAngle` which doubles the angle before
 * averaging to handle anti-parallel directions correctly (an edge has
 * no preferred direction along its axis).
 *
 * Returns null when the input is empty.
 */
export function averageDirection(refs: ReadonlyArray<{ direction: THREE.Vector3 }>): THREE.Vector3 | null {
  if (refs.length === 0) return null;
  if (refs.length === 1) {
    const d = clampToXY(refs[0].direction);
    return d ?? null;
  }
  const angle = PromptAligner.averageAngle(refs.map((r) => r.direction));
  return PromptAligner.dir3DFromAngle(angle);
}

/**
 * Perpendicular distance from `point` to the infinite line passing through
 * `lineOrigin` with direction `lineDir`. Runs in XY only. Sign-agnostic.
 */
export function perpendicularDistance(point: THREE.Vector3, lineOrigin: THREE.Vector3, lineDir: THREE.Vector3): number {
  const dir = clampToXY(lineDir);
  if (!dir) return 0;
  const normal = new THREE.Vector3(-dir.y, dir.x, 0);
  const delta = new THREE.Vector3().subVectors(point, lineOrigin);
  delta.z = 0;
  return Math.abs(delta.dot(normal));
}

/** Flatten + normalize a direction in XY. Returns null when degenerate. */
function clampToXY(v: THREE.Vector3): THREE.Vector3 | null {
  const out = new THREE.Vector3(v.x, v.y, 0);
  if (out.lengthSq() < 1e-12) return null;
  return out.normalize();
}
